import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import EmojiPicker, { Theme, EmojiClickData } from "emoji-picker-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../../components/ui/dialog";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";

export interface TagCategory {
  id: string;
  name?: string;
  color: string;
  order: number;
}

export interface Tag {
  id: string;
  label: string;
  categoryId: string;
  order: number;
}

interface CategoryCardProps {
  category: TagCategory;
  tags: Tag[];
  displayColor: string;
  onMove: (direction: "left" | "right") => void;
  onColorChange: (color: string) => void;
  onColorCommit: () => void;
  onDelete: () => void;
  onAddTag: (label: string) => void;
  onDeleteTag: (tagId: string) => void;
  onUpdateTag: (tag: Tag, newLabel: string) => void;
}

export const CategoryCard: React.FC<CategoryCardProps> = ({
  category,
  tags,
  displayColor,
  onMove,
  onColorChange,
  onColorCommit,
  onDelete,
  onAddTag,
  onDeleteTag,
  onUpdateTag,
}) => {
  const { t } = useTranslation();

  const [newTagInput, setNewTagInput] = useState("");
  const [editingTagId, setEditingTagId] = useState<string | null>(null);
  const [editingTagValue, setEditingTagValue] = useState("");
  const [emojiPicker, setEmojiPicker] = useState<"new" | "edit" | null>(null);
  const [isAddingTag, setIsAddingTag] = useState(false);

  const handleEmojiClick = (emojiData: EmojiClickData) => {
    if (emojiPicker === "new") {
      setNewTagInput((prev) => prev + emojiData.emoji);
    } else if (emojiPicker === "edit") {
      setEditingTagValue((prev) => prev + emojiData.emoji);
    }
    setEmojiPicker(null);
  };

  const handleAddTag = () => {
    const label = newTagInput.trim();
    if (!label) return;
    onAddTag(label);
    setNewTagInput("");
    setIsAddingTag(false);
  };

  const handleStartEditTag = (tag: Tag) => {
    setEditingTagId(tag.id);
    setEditingTagValue(tag.label);
  };

  const handleSaveEditTag = (tag: Tag) => {
    if (editingTagValue.trim() !== tag.label) {
      onUpdateTag(tag, editingTagValue.trim());
    }
    setEditingTagId(null);
  };

  return (
    <div
      data-testid={`category-card-${category.id}`}
      style={{
        background: "var(--color-panel-bg)",
        borderRadius: "var(--border-radius-md)",
        border: "1px solid var(--color-panel-border)",
        overflow: "hidden",
        boxShadow: "var(--shadow-card)",
      }}
    >
      {/* Header */}
      <div
        data-testid={`category-header-${category.id}`}
        style={{
          padding: "15px",
          background: "var(--color-bg-base)",
          borderBottom: "1px solid var(--color-panel-border)",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          borderTop: `4px solid ${displayColor}`,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
          <div style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
            <button
              data-testid={`category-move-left-${category.id}`}
              onClick={(e) => {
                e.stopPropagation();
                onMove("left");
              }}
              style={{
                background: "transparent",
                border: "none",
                color: "#666",
                cursor: "pointer",
                padding: 0,
                fontSize: "1rem",
                height: "12px",
                display: "flex",
                alignItems: "center",
              }}
              title={t("controller.tags.moveLeft")}
            >
              <i className="bx bx-chevron-left"></i>
            </button>
            <button
              data-testid={`category-move-right-${category.id}`}
              onClick={(e) => {
                e.stopPropagation();
                onMove("right");
              }}
              style={{
                background: "transparent",
                border: "none",
                color: "#666",
                cursor: "pointer",
                padding: 0,
                fontSize: "1rem",
                height: "12px",
                display: "flex",
                alignItems: "center",
              }}
              title={t("controller.tags.moveRight")}
            >
              <i className="bx bx-chevron-right"></i>
            </button>
          </div>
          <input
            data-testid={`category-color-input-${category.id}`}
            type="color"
            value={displayColor}
            onChange={(e) => onColorChange(e.target.value)}
            onBlur={onColorCommit}
            style={{
              width: "25px",
              height: "25px",
              borderRadius: "50%",
              border: "none",
              padding: 0,
              overflow: "hidden",
              cursor: "pointer",
              background: "transparent",
            }}
          />
          <span
            data-testid={`category-name-${category.id}`}
            style={{ fontWeight: "900", fontSize: "1rem" }}
          >
            {category.name || category.id}
          </span>
        </div>
        <button
          data-testid={`category-delete-${category.id}`}
          onClick={onDelete}
          style={{
            background: "transparent",
            border: "none",
            color: "#666",
            cursor: "pointer",
            fontSize: "1.2rem",
          }}
        >
          ×
        </button>
      </div>

      {/* Tags List */}
      <div
        data-testid={`category-tags-list-${category.id}`}
        style={{
          padding: "15px",
          display: "flex",
          flexDirection: "column",
          gap: "8px",
          minHeight: "150px",
          maxHeight: "250px",
          overflowY: "auto",
        }}
      >
        {tags.map((tag) => (
          <div
            key={tag.id}
            data-testid={`tag-item-${tag.id}`}
            style={{
              padding: "8px 12px",
              background: category.color,
              color: "#000",
              borderRadius: "6px",
              fontWeight: "bold",
              fontSize: "0.8rem",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <span
              data-testid={`tag-label-${tag.id}`}
              onClick={() => handleStartEditTag(tag)}
              style={{ cursor: "pointer", flex: 1 }}
            >
              {tag.label}
            </span>

            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <button
                data-testid={`tag-edit-button-${tag.id}`}
                onClick={(e) => {
                  e.stopPropagation();
                  handleStartEditTag(tag);
                }}
                style={{
                  background: "transparent",
                  border: "none",
                  cursor: "pointer",
                  fontSize: "1rem",
                  padding: 0,
                  opacity: 0.6,
                }}
              >
                <i className="bx bx-pencil"></i>
              </button>
              <span
                data-testid={`tag-delete-${tag.id}`}
                onClick={(e) => {
                  e.stopPropagation();
                  onDeleteTag(tag.id);
                }}
                style={{ cursor: "pointer", opacity: 0.6, fontSize: "1rem" }}
              >
                ×
              </span>
            </div>
          </div>
        ))}
        {tags.length === 0 && (
          <div
            data-testid={`category-empty-${category.id}`}
            style={{ color: "#444", textAlign: "center", fontSize: "0.8rem", padding: "10px" }}
          >
            {t("controller.tags.noTags")}
          </div>
        )}
      </div>

      {/* Add Tag Button */}
      <div
        data-testid={`category-add-tag-container-${category.id}`}
        style={{
          padding: "10px",
          borderTop: "1px solid var(--color-panel-border)",
          background: "var(--color-bg-base)",
        }}
      >
        <Button
          variant="ghost"
          className="w-full text-muted-foreground hover:text-foreground hover:bg-white/5"
          onClick={() => setIsAddingTag(true)}
          data-testid={`category-add-tag-button-${category.id}`}
        >
          {t("controller.tags.addTag", "+ Agregar Tag...")}
        </Button>
      </div>

      {/* Tag Add Dialog */}
      <Dialog
        open={isAddingTag}
        onOpenChange={(open) => {
          if (!open) {
            setIsAddingTag(false);
            setNewTagInput("");
            setEmojiPicker(null);
          }
        }}
      >
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>{t("controller.tags.addTag", "+ Agregar Tag...")}</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col gap-4 py-4">
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                className="text-xl px-3 h-10"
                onClick={() => setEmojiPicker(emojiPicker === "new" ? null : "new")}
              >
                😀
              </Button>
              <Input
                autoFocus
                value={newTagInput}
                onChange={(e) => setNewTagInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleAddTag()}
                placeholder={t("controller.tags.addTag", "+ Agregar Tag...")}
              />
            </div>
            {emojiPicker === "new" && (
              <div style={{ display: "flex", justifyContent: "center", zIndex: 100 }}>
                <EmojiPicker
                  onEmojiClick={handleEmojiClick}
                  theme={Theme.DARK}
                  width="100%"
                  height={350}
                  searchDisabled={false}
                  skinTonesDisabled
                />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setIsAddingTag(false);
                setNewTagInput("");
                setEmojiPicker(null);
              }}
            >
              {t("common.cancel", "Cancel")}
            </Button>
            <Button onClick={handleAddTag}>{t("common.save", "Save")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Tag Edit Dialog */}
      <Dialog open={!!editingTagId} onOpenChange={(open) => !open && setEditingTagId(null)}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>{t("controller.tags.editTag", "Edit Tag")}</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col gap-4 py-4">
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                className="text-xl px-3 h-10"
                onClick={() => setEmojiPicker(emojiPicker === "edit" ? null : "edit")}
              >
                😀
              </Button>
              <Input
                autoFocus
                value={editingTagValue}
                onChange={(e) => setEditingTagValue(e.target.value)}
                onKeyDown={(e) =>
                  e.key === "Enter" &&
                  tags.find((t) => t.id === editingTagId) &&
                  handleSaveEditTag(tags.find((t) => t.id === editingTagId)!)
                }
              />
            </div>
            {emojiPicker === "edit" && (
              <div style={{ display: "flex", justifyContent: "center", zIndex: 100 }}>
                <EmojiPicker
                  onEmojiClick={handleEmojiClick}
                  theme={Theme.DARK}
                  width="100%"
                  height={350}
                  searchDisabled={false}
                  skinTonesDisabled
                />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingTagId(null)}>
              {t("common.cancel", "Cancel")}
            </Button>
            <Button
              onClick={() => {
                const tagToEdit = tags.find((t) => t.id === editingTagId);
                if (tagToEdit) handleSaveEditTag(tagToEdit);
              }}
            >
              {t("common.save", "Save")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
